import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip
} from '@mui/material';
import {
  GetApp as DownloadIcon,
  Refresh as RefreshIcon,
  Description as DescriptionIcon,
  Print as PrintIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import realDataAccountingService from '../../services/realDataAccountingService';
import financialCalculationService from '../../services/financialCalculationService';
import {
  setGeneralLedger,
  setReportLoading,
  setReportError,
  setPrintData
} from '../../redux/accounting';

const GeneralLedger = () => {
  const dispatch = useDispatch();
  const { generalLedger, reportsLoading, reportsError } = useSelector(state => state.accounting);
  const { admin } = useSelector(state => state.admin);
  const { schooldetails } = useSelector(state => state.schooldetails);
  
  // Get currency from school details
  const currency = schooldetails && schooldetails.length > 0 && schooldetails[0].currency 
    ? schooldetails[0].currency 
    : (schooldetails && schooldetails.length > 0 && schooldetails[0].country === 'LR' ? 'USD' : 'GHS');
  
  const getCurrencySymbol = () => {
    return currency === 'GHS' ? '₵' : currency === 'USD' ? '$' : currency;
  };
  
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedAccount, setSelectedAccount] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const currentSchool = schooldetails && schooldetails.length > 0 ? schooldetails[0] : null;

  // Get Redux data at component level - using correct structure
  const reduxData = {
    students: useSelector(state => state.students) || [],
    teachers: useSelector(state => state.teachers) || [],
    expenses: useSelector(state => state.expenses) || [],
    sales: useSelector(state => state.sales?.sales) || [],
    assets: useSelector(state => state.assets?.assets) || []
  };

  const loadGeneralLedger = useCallback(async () => {
    if (!currentSchool?.schoolID) return;
    
    setLoading(true);
    setError(null);
    dispatch(setReportLoading({ reportType: 'generalLedger', loading: true }));
    
    try {
      // Get all financial data
      const endDateObj = new Date(endDate);
      const startDateObj = new Date(startDate);
      
      const financialData = await realDataAccountingService.getRealDashboardData(
        currentSchool?.schoolID,
        reduxData
      );

      // Generate general ledger using financial calculation service
      const ledgerData = financialCalculationService.generateGeneralLedger(
        financialData,
        startDateObj,
        endDateObj,
        selectedAccount
      );
      
      dispatch(setGeneralLedger(ledgerData));
    } catch (err) {
      setError('Failed to load general ledger');
      dispatch(setReportError({ reportType: 'generalLedger', error: err.message }));
      console.error('General ledger error:', err);
    } finally {
      setLoading(false);
      dispatch(setReportLoading({ reportType: 'generalLedger', loading: false }));
    }
  }, [currentSchool?.schoolID, startDate, endDate, selectedAccount, reduxData, dispatch]);

  useEffect(() => {
    if (currentSchool?.schoolID) {
      loadGeneralLedger();
    }
  }, [currentSchool, startDate, endDate, selectedAccount, loadGeneralLedger]);

  const handleRefresh = () => {
    loadGeneralLedger();
  };

  const handlePrint = () => {
    const printData = {
      title: 'General Ledger',
      schoolName: currentSchool?.schoolname || 'School',
      period: `${startDate} to ${endDate}`,
      data: generalLedger
    };
    dispatch(setPrintData({ generalLedger: printData }));
    window.print();
  };

  const handleExport = () => {
    // Export functionality would go here
    console.log('Exporting general ledger...');
  };

  const getAccountTypeColor = (accountType) => {
    switch (accountType) {
      case 'Asset': return 'primary';
      case 'Liability': return 'error';
      case 'Equity': return 'success';
      case 'Revenue': return 'info';
      case 'Expense': return 'warning';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={handleRefresh}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!generalLedger || !generalLedger.accounts) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">No General Ledger data available.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          <DescriptionIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          General Ledger
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
          >
            Print
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Report Filters</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Start Date"
                type="date"
                fullWidth
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="End Date"
                type="date"
                fullWidth
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Account</InputLabel>
                <Select
                  value={selectedAccount}
                  onChange={(e) => setSelectedAccount(e.target.value)}
                  label="Account"
                >
                  <MenuItem value="all">All Accounts</MenuItem>
                  {generalLedger.accounts?.map((account) => (
                    <MenuItem key={account.accountCode} value={account.accountCode}>
                      {account.accountCode} - {account.accountName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleRefresh}
                fullWidth
                sx={{ height: '56px' }}
              >
                Search
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* General Ledger */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ p: 2, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
          <Typography variant="h6">
            {currentSchool?.schoolname || 'School'} General Ledger
          </Typography>
          <Typography variant="body2">
            For the period {startDate} to {endDate}
          </Typography>
        </Box>
        
        {generalLedger.accounts?.map((account) => (
          <Box key={account.accountCode} sx={{ mb: 3 }}>
            <Box sx={{ p: 2, backgroundColor: '#e3f2fd', borderBottom: '1px solid #ddd' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h6">
                    {account.accountCode} - {account.accountName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {account.description}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Chip 
                    label={account.accountType} 
                    color={getAccountTypeColor(account.accountType)}
                    size="small"
                  />
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    Balance: {getCurrencySymbol()} {account.balance?.toLocaleString() || '0.00'}
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Reference</TableCell>
                    <TableCell align="right">Debit</TableCell>
                    <TableCell align="right">Credit</TableCell>
                    <TableCell align="right">Balance</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {account.transactions?.map((transaction, index) => (
                    <TableRow key={index}>
                      <TableCell>{transaction.date}</TableCell>
                      <TableCell>{transaction.description}</TableCell>
                      <TableCell>{transaction.reference}</TableCell>
                      <TableCell align="right">
                        {transaction.debit > 0 ? transaction.debit.toLocaleString() : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {transaction.credit > 0 ? transaction.credit.toLocaleString() : '-'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {transaction.balance?.toLocaleString() || '0.00'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!account.transactions || account.transactions.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No transactions found for this account
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ))}
      </Paper>

      {/* Summary */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>General Ledger Summary</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">
                Total Accounts: {generalLedger.accounts?.length || 0}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">
                Total Transactions: {generalLedger.totalTransactions || 0}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">
                Period: {startDate} to {endDate}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default GeneralLedger;
